import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Component, ViewChild, HostListener, AfterViewInit , CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { IDropDownItemViewEdit, IDropDownView } from "../../interfaces/idropdowns";
import { IColourPalette } from "../../interfaces/icolourpalette";
import { DropDownItem } from "../../models/dropdown";
import { DropDownItemService } from "../../services/dropdownitem.service";
import { dropDownItemTakenValidator } from "../../validators/dropdownitemtaken.validator";
import { AppSettings } from "../../appsettings";
import { TrimmedMinLengthValidator } from "../../validators/trimmedMinLength.validator";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { HeaderComponent } from "../header/header.component";

/**
 * DropDownItem Component
 */
@Component({
    selector: "dropdownitem",
    templateUrl: "./dropdownitem.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmationModalComponent, HeaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    styles: [`
        .dropdown-search-wrapper {
            position: relative;
            display: inline-block;
            width: 100%;
            z-index: 100;
        }
        
        .dropdown-search-wrapper.open {
            z-index: 100;
        }
        
        /* When modal is open, ensure dropdowns are below modal */
        body.modal-open .dropdown-search-wrapper {
            z-index: 1030 !important;
        }
        
        body.modal-open .dropdown-search-wrapper.open {
            z-index: 1030 !important;
        }
        
        /* Aggressive z-index reset for all possible loading states */
        body.loading .dropdown-search-wrapper,
        body.loading .dropdown-search-wrapper.open,
        body.loading .color-preview-input,
        body.loading .dropdown-options,
        .loading-overlay ~ .dropdown-search-wrapper,
        .loading-overlay ~ .dropdown-search-wrapper.open,
        .loading-overlay ~ .color-preview-input,
        .loading-overlay ~ .dropdown-options,
        .loading .dropdown-search-wrapper,
        .loading .dropdown-search-wrapper.open,
        .loading .color-preview-input,
        .loading .dropdown-options,
        .submitting .dropdown-search-wrapper,
        .submitting .dropdown-search-wrapper.open,
        .submitting .color-preview-input,
        .submitting .dropdown-options,
        .processing .dropdown-search-wrapper,
        .processing .dropdown-search-wrapper.open,
        .processing .color-preview-input,
        .processing .dropdown-options {
            z-index: -1 !important;
            visibility: hidden !important;
            opacity: 0 !important;
        }
        
        /* Custom dropdown arrow */
        .dropdown-arrow {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
            color: #6c757d;
            font-size: 12px;
            font-weight: normal;
        }
        
        /* Custom clear button */
        .clear-button {
            position: absolute;
            right: 35px;
            top: 50%;
            transform: translateY(-50%);
            cursor: pointer;
            color: #6c757d;
            font-size: 14px;
            transition: color 0.2s;
            background: none;
            border: none;
            padding: 0;
            line-height: 1;
        }
        
        .clear-button:hover {
            color: #495057;
        }
        
        .dropdown-search-input {
            cursor: pointer;
            border: 1px solid #d1d3d4;
            border-radius: 0;
            padding-right: 35px;
            width: 100%;
        }
        
        .dropdown-search-input.with-color-preview {
            padding-left: 30px;
        }
        
        .color-preview-input {
            position: absolute;
            left: 8px;
            top: 50%;
            transform: translateY(-50%);
            width: 15px;
            height: 15px;
            border: 1px solid #ccc;
            border-radius: 2px;
            z-index: 1;
        }
        
        .dropdown-search-input:focus {
            cursor: text;
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
            outline: 0;
            background-color: white;
        }
        
        .dropdown-search-input:hover:not(:focus) {
            border-color: #999;
        }
        
        .dropdown-options {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #d1d3d4;
            border-top: none;
            border-radius: 0;
            max-height: 200px;
            overflow-y: auto;
            z-index: 99;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        /* When modal is open, ensure dropdown options are below modal */
        body.modal-open .dropdown-options {
            z-index: 1030 !important;
        }
        
        .dropdown-option {
            padding: 6px 12px;
            cursor: pointer;
            text-align: left;
            color: #333;
            background-color: white;
            transition: background-color 0.2s;
            display: flex;
            align-items: center;
        }
        
        .color-preview {
            display: inline-block;
            width: 15px;
            height: 15px;
            margin-right: 8px;
            border: 1px solid #ccc;
            border-radius: 2px;
            flex-shrink: 0;
        }
        
        .color-description {
            flex: 1;
        }
        
        .dropdown-option:last-child {
            border-bottom: none;
        }
        
        .dropdown-option:hover {
            background-color: #0076A8;
            color: white;
        }
        
        .dropdown-option.no-results {
            color: #6c757d;
            font-style: italic;
            cursor: default;
            background-color: #f8f9fa;
        }
        
        .dropdown-option.no-results:hover {
            background-color: #f8f9fa;
            color: #6c757d;
        }
        
        /* Remove default form-control styling that makes it look disabled */
        .dropdown-search-input[readonly] {
            background-color: white !important;
            opacity: 1 !important;
            color: #333 !important;
            cursor: pointer !important;
        }
        
        /* Square input styling */
        .form-control.dropdown-search-input {
            height: 38px;
            line-height: 1.5;
            border-radius: 0;
            width: 100%;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            background-image: none !important;
        }
        
        /* Remove all form-control arrows */
        .form-control.dropdown-search-input::-webkit-outer-spin-button,
        .form-control.dropdown-search-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        
        .form-control.dropdown-search-input::-ms-expand {
            display: none;
        }
    `]
})
export class DropDownItemComponent implements ICanComponentDeactivate, AfterViewInit {
    dropDown: IDropDownView;
    dropDownItem: IDropDownItemViewEdit;
    colourPalettesDropDown: DropDownItem[];
    filteredColourPalettes: DropDownItem[] = [];
    colourPalette: IColourPalette[];
    selectedColourPalette: DropDownItem = null;
    dropDownItemForm: FormGroup;
    usedColours: string[];
    originalData:string;
    
    // Searchable dropdown properties
    showColourDropdownOptions: boolean = false;
    colourSearchTerm: string = '';
    isColourInputFocused: boolean = false;
    tempSelectedColourPalette: DropDownItem = null;

    @ViewChild("confirmModal") confirmModal;
    /**
     * Initiates the form controls
     * @param activatedRoute Contains the selected drop down and colour palettes
     * @param dropDownItemService Used for all drop down item related operations
     * @param toastsManager Displays messages to the user
     * @param router Used for navigation
     */
    constructor(private activatedRoute: ActivatedRoute,
        private dropDownItemService: DropDownItemService,
        private toastsManager: ToastrService,
        private router: Router) {
        this.dropDown = activatedRoute.snapshot.data["dropDown"] as IDropDownView;
        this.colourPalette = activatedRoute.snapshot.data["colourPalettes"] as IColourPalette[];
        // here we only want to show colours that have not been used already
        if (this.dropDown.items != null) {
            this.usedColours = this.getUsedColours();
        }

        this.colourPalettesDropDown = this.getColourPaletteDropDown();
        this.filteredColourPalettes = [...this.colourPalettesDropDown]; // Initialize filtered colors

        if (this.activatedRoute.snapshot.data["dropDownItem"] != null) {
            this.dropDownItem = activatedRoute.snapshot.data["dropDownItem"];
            // get selected drop down for colour palette
            if (this.dropDownItem.dropDown.presentationTypeId === 2) {
                if (this.dropDownItem.colour != null) {
                    this.selectedColourPalette = this.colourPalettesDropDown.filter(co =>
                        this.colourPalette.filter(c => c.colour === this.dropDownItem.colour)[0].id === co.id)[0] as DropDownItem;
                }
            }
        }

        if (this.selectedColourPalette == null) {
            this.selectedColourPalette = this.colourPalettesDropDown[0] as DropDownItem;
        }
        this.dropDownItemForm = new FormGroup({
            text: new FormControl(this.dropDownItem ? this.dropDownItem.text : "", [
                Validators.required,
                TrimmedMinLengthValidator(2),
                Validators.pattern(AppSettings.alphaNumeric.source),
                dropDownItemTakenValidator(this.dropDown, (this.dropDownItem ? this.dropDownItem.id : null))
            ]),
            code: new FormControl(this.dropDownItem ? this.dropDownItem.code : "", [
                Validators.pattern(AppSettings.alphaNumeric.source)
            ]),
            colour: new FormControl(this.selectedColourPalette, this.dropDown.presentationTypeId === 2 ? [Validators.required] : []), // Set the form control to the selected object
            id: new FormControl(this.dropDownItem ? this.dropDownItem.id : null),
            disabled: new FormControl(this.dropDownItem ? this.dropDownItem.disabled : false)
        });
    }

    ngAfterViewInit(): void {
        this.originalData = JSON.stringify(this.dropDownItemForm.value);
    }

    /**
     * Saves the values from the form
     * @param item The item to create/update
     * @param valid True if the form is valid, false if it is not
     */
    saveClicked(item: IDropDownItemViewEdit, valid: boolean): void {
        // Close any open dropdowns before submitting
        this.showColourDropdownOptions = false;
        this.isColourInputFocused = false;
        
        if (valid) {
            if (this.dropDown.presentationTypeId === 2) {
                // Get the colour from the form control value (which is now a DropDownItem object)
                const selectedColourItem = this.dropDownItemForm.get('colour').value;
                if (selectedColourItem && selectedColourItem.id) {
                    item.colour = this.colourPalette.filter(cp => cp.id === selectedColourItem.id)[0].colour;
                } else {
                    item.colour = null;
                }
            } else {
                item.colour = null;
            }

            if (item.id == null) {
                item.dropDownId = this.dropDown.id;
                // create
                this.dropDownItemService.create(item).subscribe(() => {
                    this.toastsManager.success("Drop down item created");
                    this.originalData = JSON.stringify(this.dropDownItemForm.value);
                    this.router.navigate(["/dropdown", this.dropDown.id]);
                });
            } else {
                // update
                this.dropDownItemService.update(item).subscribe(() => {
                    this.toastsManager.success("Drop down item updated");
                    this.originalData = JSON.stringify(this.dropDownItemForm.value);
                    this.router.navigate(["/dropdown", this.dropDownItem.dropDownId]);
                });
            }
        }
    }

    /**
     * Gets called when a colour is selected from the drop down
     * @param colour The colour selected from the drop down
     */
    colourSelected(colour: DropDownItem): void {
        this.selectedColourPalette = colour;
        this.tempSelectedColourPalette = null;
        this.colourSearchTerm = '';
        this.showColourDropdownOptions = false;
        this.isColourInputFocused = false;
        // Update the form control with the selected colour object
        this.dropDownItemForm.get('colour').setValue(colour);
    }

    /**
     * Handle search input for colour dropdown
     * @param event The input event from the search field
     */
    onColourSearchInput(event: any): void {
        this.colourSearchTerm = event.target.value;
        this.showColourDropdownOptions = true;
        this.filterColourPalettes();
        
        // Clear selected colour when user starts typing
        if (this.selectedColourPalette && this.colourSearchTerm.trim() !== '') {
            this.tempSelectedColourPalette = this.selectedColourPalette;
            this.selectedColourPalette = null;
        }
    }

    /**
     * Filter colour palettes based on search term
     */
    filterColourPalettes(): void {
        if (!this.colourSearchTerm || this.colourSearchTerm.trim() === '') {
            this.filteredColourPalettes = [...this.colourPalettesDropDown];
        } else {
            this.filteredColourPalettes = this.colourPalettesDropDown.filter(colour => {
                // Use searchableText if available, otherwise fall back to regular text
                const searchText = (colour as any).searchableText || colour.text;
                return searchText.toLowerCase().includes(this.colourSearchTerm.toLowerCase());
            });
        }
    }

    /**
     * Handle input blur event for colour dropdown
     */
    onColourInputBlur(): void {
        // Mark as touched for validation
        this.dropDownItemForm.get('colour').markAsTouched();
        
        // Small delay to allow click events on dropdown options
        setTimeout(() => {
            this.showColourDropdownOptions = false;
            this.isColourInputFocused = false;
            
            // If no colour is selected and we have a temp selection, restore it
            if (!this.selectedColourPalette && this.tempSelectedColourPalette) {
                this.selectedColourPalette = this.tempSelectedColourPalette;
                this.tempSelectedColourPalette = null;
                this.colourSearchTerm = '';
            } else if (!this.selectedColourPalette) {
                this.colourSearchTerm = '';
            }
        }, 150);
    }

    /**
     * Handle input click event for colour dropdown
     */
    onColourInputClick(): void {
        if (this.selectedColourPalette) {
            this.tempSelectedColourPalette = this.selectedColourPalette;
            this.selectedColourPalette = null;
            this.colourSearchTerm = '';
        }
        this.isColourInputFocused = true;
        this.showColourDropdownOptions = true;
        this.filterColourPalettes();
    }

    /**
     * Handle input focus for colour dropdown
     */
    onColourInputFocus(): void {
        if (this.selectedColourPalette) {
            this.tempSelectedColourPalette = this.selectedColourPalette;
            this.selectedColourPalette = null;
            this.colourSearchTerm = '';
        }
        this.isColourInputFocused = true;
        this.showColourDropdownOptions = true;
    }

    /**
     * Handle keydown events for colour dropdown
     * @param event The keyboard event
     */
    onColourKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.showColourDropdownOptions = false;
            this.isColourInputFocused = false;
            (event.target as HTMLInputElement).blur();
            
            // Restore temp selection if no colour is selected
            if (!this.selectedColourPalette && this.tempSelectedColourPalette) {
                this.selectedColourPalette = this.tempSelectedColourPalette;
                this.tempSelectedColourPalette = null;
                this.colourSearchTerm = '';
            } else if (!this.selectedColourPalette) {
                this.colourSearchTerm = '';
            }
        }
    }

    /**
     * Clear selected colour
     */
    clearColourSelection(): void {
        this.selectedColourPalette = null;
        this.tempSelectedColourPalette = null;
        this.colourSearchTerm = '';
        this.isColourInputFocused = false;
        this.showColourDropdownOptions = false;
        this.dropDownItemForm.get('colour').setValue(null);
        this.dropDownItemForm.get('colour').markAsTouched(); // Mark as touched for validation
    }

    /**
     * Get the hex color value for a colour palette ID
     * @param colourId The colour palette ID
     */
    getColourHex(colourId: number): string {
        const colourPalette = this.colourPalette.find(cp => cp.id === colourId);
        return colourPalette ? colourPalette.colour : '#ffffff';
    }

    /**
     * Get the description for a colour palette ID
     * @param colourId The colour palette ID
     */
    getColourDescription(colourId: number): string {
        const colourPalette = this.colourPalette.find(cp => cp.id === colourId);
        return colourPalette ? colourPalette.description : '';
    }

    /**
     * Get the display text for the input field
     */
    getDisplayText(): string {
        if (this.selectedColourPalette) {
            return this.getColourDescription(this.selectedColourPalette.id);
        }
        return this.colourSearchTerm;
    }

    /**
     * @internal
     * Gets the used colours for the drop down items to prevent
     * the same colour being used
     */
    private getUsedColours(): string[] {
        let selectedColours: Array<string> = new Array<string>();
        this.dropDown.items.forEach(e => {
            if (!e.disabled) {
                selectedColours.push(e.colour);
            }
        });
        return selectedColours;
    }

    /**
     * @internal
     * Returns a list of colour palette drop down items that haven"t already been used
     */
    private getColourPaletteDropDown(): DropDownItem[] {
        let availableColourPalettes: Array<DropDownItem> = Array<DropDownItem>();
        this.colourPalette.map((colourPalette) => {
            const searchableText = `${colourPalette.description} ${colourPalette.colour}`;
            const dropdownItem = new DropDownItem(colourPalette.id, colourPalette.description, null);
            // Add searchable text property for better filtering
            (dropdownItem as any).searchableText = searchableText;
            availableColourPalettes.push(dropdownItem);
        });
        return availableColourPalettes;
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        let changedSet: string = JSON.stringify(this.dropDownItemForm.value);
        if (this.originalData !== changedSet) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.originalData = JSON.stringify(this.dropDownItemForm.value);
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }
    navigatingTo: string;
}



